import MxDetailPanelAction, {
  MxDetailPanelActionItem
} from "@rf-l1/page/details/MxDetailPanelAction";
import {
  IconShare,
  IconPercentage,
  IconAlertCircle,
  IconEdit,
  IconClockPlay,
  IconRefresh,
  IconFileTypePdf,
  IconFileDownload,
  IconTrash,
  IconAlertTriangle,
  IconExternalLink,
  IconMailForward
} from "@tabler/icons-react";
import { usePropertyDetailPage } from "../../context/PropertyDetailPageContext";
import ENVIRONMENT_VAR from "@/src-refactor/modules/shared/model/const/env/env.const";
import usePropertyImagesDownload from "@rf/property/module/hooks/usePropertyImagesDownload/usePropertyImagesDownload";
import usePermissionProperty from "@/src-refactor/modules/private/properties/hooks/usePermissionProperty/usePermissionProperty";
import RemaxLogo from "@icons-rf/remax-logo.svg?react";
import usePropertyBrochurePdf from "@rf/property/module/hooks/usePropertyBrochurePdf";
import useReadOnlyModeGuard from "@rf-hooks/useReadOnlyModeGuard";

/**
 * PropertyActionsPanel
 *
 * Panel de acciones lateral para la vista de detalle de propiedad.
 * Replica la funcionalidad del PropertyPanel pero usando el componente
 * MxDetailPanelAction para una UI más moderna y consistente.
 *
 * @component
 *
 * Funcionalidades principales:
 * - Referir propiedad
 * - Reportar comisión
 * - Reportar problemas
 * - Editar propiedad
 * - Activar/Extender disponibilidad
 * - Descargar PDF e imágenes
 * - Dar de baja
 * - Solicitar reactivación
 * - Ver en RE/MAX RD
 * - Resolver conflictos
 *
 * Permisos basados en:
 * - Rol del usuario (Super Admin, Admin, Agente)
 * - Estado de la propiedad (Activa, Pendiente, Baja, etc.)
 * - Relación con la propiedad (Captador, Agencia)
 */
const PropertyActionsPanel = () => {
  const propertyData = usePropertyDetailPage();
  const { guardReadOnlyAction } = useReadOnlyModeGuard();

  // Hooks de acciones
  const { downloadImages } = usePropertyImagesDownload();
  const { getPermissions } = usePermissionProperty();
  const { generateBrochure } = usePropertyBrochurePdf();

  // Datos de la propiedad
  const {
    id,
    status,
    isProject,
    propertyType,
    images,
    agents = [],
    isCollection,
    brochure,
  } = propertyData;

  // Obtener permisos usando el hook centralizado
  const permissions = getPermissions({
    idStatusProperty: status?.id || 0,
    isProject: Boolean(isProject),
    agents: agents,
    hasImages: images && images.length > 0
  });

  // Handlers de acciones
  const handleEdit = () => {
    guardReadOnlyAction("editar propiedad", "property-detail-actions");
  };

  const handleReportCommission = () => {
    guardReadOnlyAction("reportar comisión", "property-detail-actions");
  };

  const handleReportProperty = () => {
    guardReadOnlyAction("reportar propiedad", "property-detail-actions");
  };

  const handleViewOnWebsite = () => {
    const url = `${ENVIRONMENT_VAR.REMAX_DOMAIN}${
      isCollection ? "/collection" : ""
    }/propiedad/${propertyType
      ?.replaceAll(" ", "-")
      .replaceAll("/", "o")
      .toLowerCase()}/${id}`;
    window.open(url, "_blank");
  };

  const handleResolveConflict = () => {
    guardReadOnlyAction("resolver conflicto", "property-detail-actions");
  };

  const handleDownloadImages = async () => {
    await downloadImages(images, id);
  };

  const handleDrop = () => {
    guardReadOnlyAction("dar de baja propiedad", "property-detail-actions");
  };

  const handleRefer = () => {
    guardReadOnlyAction("referir propiedad", "property-detail-actions");
  };

  const handleRequestReactivation = () => {
    guardReadOnlyAction("solicitar reactivación", "property-detail-actions");
  };

  // Definición de acciones
  const actions: MxDetailPanelActionItem[] = [
    // Referir propiedad
    {
      label: "Referir",
      icon: <IconShare size={20} />,
      onClick: handleRefer,
      show: permissions.canReferProperty
    },
    // Reportar comisión
    {
      label: "Reportar comisión",
      icon: <IconPercentage size={20} />,
      onClick: handleReportCommission,
      show: permissions.canReportCommission
    },
    // Reportar propiedad
    {
      label: "Reportar propiedad",
      icon: <IconAlertCircle size={20} />,
      onClick: handleReportProperty,
      show: permissions.canReportProperty
    },
    // Editar propiedad
    {
      label: "Editar propiedad",
      icon: <IconEdit size={20} />,
      onClick: handleEdit,
      show: permissions.canUpdateProperty
    },
    // Resolver conflicto
    {
      label: "Resolver conflicto",
      icon: <IconAlertTriangle size={20} />,
      onClick: handleResolveConflict,
      show: permissions.canResolveConflict
    },

    // Activar y extender disponibilidad
    {
      label: "Activar y extender disponibilidad",
      icon: <IconRefresh size={20} />,
      onClick: () =>
        guardReadOnlyAction(
          "activar y extender disponibilidad",
          "property-detail-actions"
        ),
      show: permissions.canExtendTimeAndActivate
    },
    // Extender disponibilidad
    {
      label: "Extender disponibilidad",
      icon: <IconClockPlay size={20} />,
      onClick: () =>
        guardReadOnlyAction("extender disponibilidad", "property-detail-actions"),
      show: permissions.canExtendTime
    },

    {
      show: permissions.canActivateProperty,
      label: "Aprobar propiedad",
      icon: <IconRefresh size={20} />,
      onClick: () =>
        guardReadOnlyAction("aprobar propiedad", "property-detail-actions")
    },

    // Descargar PDF
    {
      label: "Descargar PDF",
      icon: <IconFileTypePdf size={20} />,
      onClick: () => {
        generateBrochure(brochure);
      },
      show: permissions.canDownloadPDF
    },
    // Descargar imágenes
    {
      label: "Descargar imágenes",
      icon: <IconFileDownload size={20} />,
      onClick: handleDownloadImages,
      show: permissions.canDownloadImages
    }
  ].filter(action => action.show !== false);

  return (
    <MxDetailPanelAction
      title="PANEL DE LA PROPIEDAD"
      actions={actions}
      buttonsActions={[
        {
          label: "DAR DE BAJA",
          onClick: handleDrop,
          show: permissions.canDropProperty,
          variant: "contained" as const,
          color: "error" as const
        },
        {
          label: "SOLICITAR REACTIVACIÓN",
          onClick: handleRequestReactivation,
          show: permissions.canRequestReactivation,
          variant: "contained" as const,
          color: "primary" as const
        },
        {
          label: "Ver en RE/MAX RD",
          onClick: handleViewOnWebsite,
          show: permissions.canViewOnWebsite,
          variant: "contained" as const,
          color: "primary" as const,
          startIcon: <RemaxLogo width={18} height={18} />
        }
      ]}
    />
  );
};

export default PropertyActionsPanel;
